import { Guid } from '../../../../store/model/guid';
import { isCompleted } from '../../models/ItemsCompletion';
import { UserProgressService } from '../UserProgressService';
import { findHeaviestPathInTree } from './findHeaviestPathInTree';
import { findSection } from './findSection';
import { ensureSection, isSection, isSequencedFlowItem, Item, Section, SequencedFlowItem } from '@ael/core-api';
import { List } from 'immutable';

export class Segment {
  content: Guid[];
  size: number;
  sections: Section[];
  constructor(items: Item[]) {
    this.content = items.filter((item) => !isSection(item)).map((item) => item.id);
    this.sections = items
      .filter((item) => isSection(item))
      .map((item) => {
        ensureSection(item);
        return item;
      });
    this.size = this.content.length;
  }

  hasBranches() {
    return this.sections.length > 0;
  }

  findBranch(branchId: Guid) {
    return this.sections.find((section: Section) => section.id === branchId);
  }
}

/**
 * Segment conversions and content completion results, shared by every evaluator run that belongs to
 * one tracking generation. Held by the owning service rather than by a single `run()`, which used to
 * throw both caches away although consecutive runs traverse overlapping subtrees.
 */
export type SequencedFlowEvaluatorCaches = {
  segments: Map<Guid, Segment>;
  contentCompletion: Map<Guid, boolean>;
};

export const createSequencedFlowEvaluatorCaches = (): SequencedFlowEvaluatorCaches => ({
  segments: new Map<Guid, Segment>(),
  contentCompletion: new Map<Guid, boolean>(),
});

export class SequencedFlowContentEvaluator {
  private readonly _statusEvaluator: UserProgressService;
  private readonly _getItem: (id: Guid) => Item;
  private readonly _evaluatedSegment: SequencedFlowItem | Section;
  // Required to calculate overall progress, as we need to find preceding segments
  private readonly _sequencedFlowItem?: SequencedFlowItem;
  // Cache for segment conversions to avoid redundant work
  private readonly _segmentCache: Map<Guid, Segment>;
  // Cache for content completion checks
  private readonly _contentCompletionCache: Map<Guid, boolean>;

  constructor(
    item: SequencedFlowItem | Section,
    statusEvaluator: UserProgressService,
    getItem: (id: Guid) => Item,
    sequencedFlowItem?: SequencedFlowItem,
    caches: SequencedFlowEvaluatorCaches = createSequencedFlowEvaluatorCaches()
  ) {
    this._segmentCache = caches.segments;
    this._contentCompletionCache = caches.contentCompletion;
    this._sequencedFlowItem = sequencedFlowItem;
    this._getItem = getItem;
    this._statusEvaluator = statusEvaluator;
    this._evaluatedSegment = item;
  }

  run() {
    // Check if any branch is fully completed to the end
    if (this.isAnyBranchFullyCompleted(this._evaluatedSegment)) {
      return {
        itemsCount: 1,
        completedItemsCount: 1,
      };
    }

    const longestPathToComplete = this.getLongestPathToEnd(this._evaluatedSegment);
    const previousSections = this.calculatePrecedingContentCount();
    const completed = this.calculateCompletedContentItems(longestPathToComplete.itemIds);

    return {
      itemsCount: previousSections + longestPathToComplete.itemIds.length,
      completedItemsCount: previousSections + completed,
    };
  }

  private getLongestPathToEnd(item: Section | SequencedFlowItem): {
    itemIds: Guid[];
  } {
    const longestPath = findHeaviestPathInTree(
      this.convertSequencedSectionIntoNode(item),
      (node) => !node.hasBranches(),
      (node) => node.sections.map(this.convertSequencedSectionIntoNode),
      (node) => node.size
    );
    const itemsInLongestNode = longestPath.branches.reduce((items: string[], node: Segment): string[] => {
      return [...node.content, ...items];
    }, []);

    return { itemIds: itemsInLongestNode };
  }

  private calculatePrecedingContentCount() {
    if (!this._sequencedFlowItem) {
      return 0;
    }
    const pathToCurrent = findSection(this._evaluatedSegment, this._sequencedFlowItem, this._getSubnodes);
    if (!pathToCurrent) {
      return 0;
    }

    return (
      this.getSectionContentCount(pathToCurrent) + this.convertSequencedSectionIntoNode(this._sequencedFlowItem).size
    );
  }

  private getSectionContentCount = (sectionsToGoThrough: List<Guid>) => {
    return sectionsToGoThrough.reduce((total: number, sectionId: Guid) => {
      const section = this._getItem(sectionId);
      ensureSection(section);
      const segment = this.convertSequencedSectionIntoNode(section);
      return total + segment.size;
    }, 0);
  };

  private calculateCompletedContentItems = (itemIds: Guid[]) => {
    let completedCount = 0;
    for (const itemId of itemIds) {
      const item = this._getItem(itemId);
      if (!isSection(item) && this.isContentItemCompleted(itemId)) {
        completedCount++;
      }
    }
    return completedCount;
  };

  private convertSequencedSectionIntoNode = (item: SequencedFlowItem | Section): Segment => {
    const cached = this._segmentCache.get(item.id);
    if (cached) {
      return cached;
    }
    const itemsInSegment = isSequencedFlowItem(item) ? item.contentItems : item.children;
    const node = new Segment(itemsInSegment.map(this._getItem).toArray());
    this._segmentCache.set(item.id, node);
    return node;
  };

  private _getSubnodes = (item: SequencedFlowItem | Section): Section[] | null => {
    const node = this.convertSequencedSectionIntoNode(item);

    return node.hasBranches() ? node.sections : null;
  };

  private isContentItemCompleted(itemId: Guid): boolean {
    const cached = this._contentCompletionCache.get(itemId);
    if (cached !== undefined) {
      return cached;
    }
    const contentItem = this._getItem(itemId);
    const result = this._statusEvaluator.evaluate(contentItem);
    const completed = isCompleted(result);
    this._contentCompletionCache.set(itemId, completed);
    return completed;
  }

  private isAnyBranchFullyCompleted(item: Section | SequencedFlowItem): boolean {
    const segment = this.convertSequencedSectionIntoNode(item);

    // If segment is empty (no content and no branches), it cannot be considered completed
    if (segment.content.length === 0 && !segment.hasBranches()) {
      return false;
    }

    // Check if all content items in the current segment are completed
    const allContentCompleted = segment.content.every((itemId: Guid) => this.isContentItemCompleted(itemId));

    // If there are no branches, check if current segment is completed
    if (!segment.hasBranches()) {
      return allContentCompleted;
    }

    // If current segment content is not completed, no branch can be completed
    if (!allContentCompleted) {
      return false;
    }

    // Check if any branch section is fully completed recursively
    return segment.sections.some((section: Section) => {
      return this.isAnyBranchFullyCompleted(section);
    });
  }
}
